import React, { useState, useEffect } from 'react';
import { Send, AlertCircle } from 'lucide-react';
import { useDesignColors } from '../../hooks/useDesignColors';
import { useCustomTexts } from '../../hooks/useCustomTexts';
import { useThemeSettings } from '../../hooks/useThemeSettings';
import { useChatMessages } from '../../hooks/useChatMessages';

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
}

export function ChatInput({ onSendMessage, disabled }: ChatInputProps) {
  const [message, setMessage] = useState('');
  const { colors } = useDesignColors();
  const { inputPlaceholder, errorMessage } = useCustomTexts();
  const { theme } = useThemeSettings();
  const { isLimitReached } = useChatMessages();
  const isDark = theme.mode === 'dark';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !disabled && !isLimitReached) {
      onSendMessage(message);
      setMessage('');
    }
  };

  return (
    <div className="space-y-2">
      {isLimitReached && (
        <div className={`rounded-lg px-4 py-2 flex items-center gap-2 ${
          isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600'
        }`}>
          <AlertCircle className="w-4 h-4 shrink-0" />
          <p className="text-sm">
            {errorMessage}
          </p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="flex space-x-2">
        <input
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={inputPlaceholder}
          disabled={disabled || isLimitReached}
          className={`flex-1 h-11 rounded-lg px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-transparent disabled:opacity-50 ${
            isDark 
              ? 'bg-gray-700/50 border-gray-600 text-white placeholder-gray-400' 
              : 'bg-gray-300 border-gray-300 text-gray-700 placeholder-gray-500'
          }`}
        />
        <button
          type="submit"
          disabled={!message.trim() || disabled || isLimitReached}
          style={{
            background: `linear-gradient(315deg, ${colors.gradientEnd}, ${colors.gradientStart})`,
            opacity: !message.trim() ? 0.5 : 0.9,
          }}
          className="h-11 px-4 rounded-lg text-white transition-all hover:opacity-100 flex items-center justify-center disabled:cursor-not-allowed"
        >
          <Send className="w-5 h-5" />
        </button>
      </form>
    </div>
  );
}